import type {
    IAddPriceReq,
    IFindManyReq,
    IProduceTypeBaselinePrice,
    IProduceTypeCreateInput,
    IProduceTypeUpdateInput,
} from "@agrigate/product-catalog-api/lib/services/ProduceTypesService";
import type { IExtProduceType } from "@agrigate/product-catalog-types";
import type { BaseCollectionQueryOpts } from "@agrigate/shared/lib/hooks/use-collection-query";
import { usePaginatedCollectionQuery } from "@agrigate/shared/lib/hooks/use-collection-query";
import type {
    EntityKeyRec,
    QueryResultsRec,
} from "@agrigate/shared/lib/utils/query";
import type { WithOptional } from "@agrigate/ts-utils/lib/types";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import rpc from "../utils/rpc";

export interface ILExtProduceType extends IExtProduceType {
    prices?: IProduceTypeBaselinePrice[];
}

export const produceTypeByIdAtom = atom<EntityKeyRec<ILExtProduceType>>({});
export const ptQueriesAtom = atom<
    QueryResultsRec<IFindManyReq, ILExtProduceType>
>({});

type ProduceTypeQueryOpts = BaseCollectionQueryOpts<object, IExtProduceType>;

const defaultPTQuery = {
    enrichments: {
        varieties: true,
        categories: true,
        zohoLinking: true,
    },
};

export const useProduceTypesQuery = (
    opts: WithOptional<ProduceTypeQueryOpts, "fetch" | "query">,
) =>
    usePaginatedCollectionQuery({
        entitiesAtom: produceTypeByIdAtom,
        queriesAtom: ptQueriesAtom,
        queryId: "$default",
        query: defaultPTQuery,
        fetch: async query => {
            const rows = await rpc.produceTypes.findMany(query);
            return rows.produceTypes.items;
        },
        ...opts,
    });

export const useProduceType = (opts: { id?: string; autoFetch?: boolean }) => {
    const [mapping, setMapping] = useAtom(produceTypeByIdAtom);
    const { id } = opts;
    const item = id ? mapping[id] : null;

    useEffect(() => {
        if (!id || item || opts.autoFetch === false) return;
        const fetchProduceType = async () => {
            const { produceType } = await rpc.produceTypes.findOneById({ id });
            if (!produceType) return;
            setMapping(prev => ({
                ...prev,
                [produceType.id]: produceType,
            }));
        };
        fetchProduceType();
    }, [item, opts.autoFetch]);

    const updateItem = (
        update: (prev: ILExtProduceType) => ILExtProduceType,
    ) => {
        if (!id) return;
        setMapping(prev => {
            const prevItem = prev[id];
            if (prevItem)
                return {
                    ...prev,
                    [id]: update(prevItem),
                };
            return prev;
        });
    };

    const fetchPrices = async () => {
        if (!id) return;
        const { items: prices } = await rpc.produceTypes.findPrices({ id });
        updateItem(prev => ({
            ...prev,
            prices,
        }));
    };

    const addPrice = async (input: IAddPriceReq) => {
        const { entity } = await rpc.produceTypes.addPrice(input);
        updateItem(prev => ({
            ...prev,
            prices: (prev.prices ?? []).concat(entity),
        }));
    };

    const updatePrice = async (id: string, pricePerUnit: string) => {
        const { entity } = await rpc.produceTypes.updatePrice({
            id,
            pricePerUnit,
        });
        updateItem(prev => ({
            ...prev,
            prices: prev.prices?.map(it => {
                if (it.id === entity.id) return entity;
                return it;
            }),
        }));
    };

    const createOne = async (input: IProduceTypeCreateInput) => {
        const { entity } = await rpc.produceTypes.createOne({ input });
        updateItem(prev => ({
            ...prev,
            ...entity,
        }));
    };

    const updateOne = async (input: IProduceTypeUpdateInput) => {
        const { entity } = await rpc.produceTypes.updateOne({ input });
        updateItem(prev => ({
            ...prev,
            ...entity,
        }));
    };

    return {
        item,
        updateItem,
        updatePrice,
        addPrice,
        fetchPrices,
        createOne,
        updateOne,
    };
};
